import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';

import {
  Button,
  Card,
  DataTable,
  DataTableColumn,
  DataTableCell,
  MediaObject,
  Spinner
} from '@salesforce/design-system-react';

import { UploadCatalogHelper } from '../index';

import './CatalogList.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const badgeFor = status => {
    switch (status.statusIcon) {
      case 'importing':
        return 'slds-theme_inverse';
      case 'complete':
        return 'slds-theme_success';
      case 'error':
        return 'slds-theme_error';
      case 'complete_with_error':
        return 'slds-theme_warning';
      default:
        return '';
    }
  };

  switch (property) {
    case 'status':
      let onClick = () => {};
      let clickableClass = '';
      if (['error', 'complete_with_error'].includes(props.item.statusIcon)) {
        const clickable =
          props.item.status_errors && props.item.status_errors.length;
        if (clickable) {
          onClick = () => props.onDetailsClick(props.item);
          clickableClass = 'clickable-badge';
        }
      }
      const badge = badgeFor(props.item);
      return (
        <td>
          <span
            className={`catalog-badge-cell slds-badge ${badge} ${clickableClass}`}
            onClick={onClick}
          >
            {children}
          </span>
        </td>
      );
    default: {
      return <td className="catalog-cell wrap-text">{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object
};

class CatalogList extends Component {
  constructor(props) {
    super(props);
    this.inputFileRef = React.createRef();
  }
  headerCell(property, formatMessage) {
    return formatMessage({ id: `components.ecs.catalogs.${property}` });
  }

  columns() {
    return {
      filename: { sortable: true },
      status: { sortable: true, width: '250px' },
      rowCount: { sortable: false },
      createdAt: { sortable: true }
    };
  }

  tableData(items) {
    const { formatMessage } = this.props.intl;
    return items.map(item => ({
      ...item,
      statusIcon: item.status,
      status: formatMessage({
        id: `components.ecs.catalogs.statuses.${item.status}`
      }),
      createdAt: formatTimestamp(this.props.intl)(item.created_at),
      rowCount:
        item.imported_count == null
          ? item.row_count
          : formatMessage(
              { id: 'components.ecs.catalogs.xOfY' },
              { x: item.imported_count, y: item.row_count }
            ),
      id: item.id.toString()
    }));
  }

  header(items) {
    const count = items ? items.length : 0;
    return (
      <div className="catalog-header">
        <h2>
          <FormattedMessage id="components.ecs.catalogs.header" />
        </h2>
        <FormattedMessage
          id="components.ecs.catalogs.items"
          values={{ count }}
        />
      </div>
    );
  }

  renderTable(items) {
    const { formatMessage } = this.props.intl;
    return (
      <Card
        className="catalog-card"
        header={<MediaObject body={this.header(items)} />}
        headerActions={
          <Button
            label={formatMessage({
              id: 'components.ecs.catalogs.button'
            })}
            onClick={() => this.inputFileRef.current.click()}
          />
        }
      >
        <DataTable items={items} className="catalog-list-table" fixedLayout>
          {Object.entries(this.columns()).map((column, index) => {
            const sortable = column[1].sortable;
            const width = column[1].width;
            const property = column[0];
            return (
              <DataTableColumn key={index} sortable={sortable}>
                <CustomDataTableCell
                  property={property}
                  formatMessage={formatMessage}
                  label={this.headerCell(property, formatMessage)}
                  width={width}
                  onDetailsClick={this.props.onDetailsClick}
                />
              </DataTableColumn>
            );
          })}
        </DataTable>
      </Card>
    );
  }

  drawSpinner(isLoading, hasLoadedOnce) {
    return isLoading && !hasLoadedOnce ? (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    ) : null;
  }

  render() {
    const { isLoading, hasLoadedOnce, catalogs } = this.props;
    const canRender = !!catalogs;

    return (
      <React.Fragment>
        {canRender ? this.renderTable(this.tableData(catalogs)) : null}
        {this.drawSpinner(isLoading, hasLoadedOnce)}
        <UploadCatalogHelper
          actions={this.props.actions}
          _ref={this.inputFileRef}
        />
      </React.Fragment>
    );
  }
}

CatalogList.contextTypes = {
  router: PropTypes.object
};

CatalogList.propTypes = {
  catalogs: PropTypes.array.isRequired,
  actions: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  hasLoadedOnce: PropTypes.bool.isRequired,
  onDetailsClick: PropTypes.func.isRequired,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(CatalogList);
